import React, {useState, useEffect, useCallback, useRef} from 'react';

import {addGenericErrorToast} from '@albert-io/atomic';
import notifier from '@albert-io/notifier';
import {refreshSession} from '@albert-io/mandark/authentication';
import {type UserModelV2} from 'resources/augmented/User/UserModel.v2';
import {redirect} from 'client/User/UserRedirectUtil';

import {
  useCurrentUserPendingTasksQuery,
  useCurrentUserCoppaConsentGrantsQuery
} from '../../signUp.queries.generated';
import {usePerformSignupStep} from '../../usePerformSignupStep';
import {SIGNUP_TASKS, COPPA_AGE_THRESHOLD, type PersonalInfoData} from '../onboarding.types';

import AgeCheckStep from './AgeCheckStep';
import ConsentOptionStep, {type ConsentOption} from './ConsentOptionStep';
import ConfirmInfoStep from './ConfirmInfoStep';
import NewEnrollCodeStep from './NewEnrollCodeStep';
import NewClassInviteStep from './NewClassInviteStep';
import NewParentalConsentStep from './NewParentalConsentStep';

type OnboardingStep = 'age-check' | 'consent-option' | 'consent-sub-flow' | 'confirm-info';

interface NewStudentOnboardingProps {
  user: UserModelV2;
  onComplete: () => void;
}

function getInitialStep(pendingTasks: string[]): OnboardingStep {
  if (pendingTasks.includes(SIGNUP_TASKS.COPPA_AGE_SCREENING)) {
    return 'age-check';
  }
  if (pendingTasks.includes(SIGNUP_TASKS.PARENTAL_CONSENT)) {
    return 'consent-option';
  }
  if (pendingTasks.includes(SIGNUP_TASKS.ACCOUNT_COMPLETION)) {
    return 'confirm-info';
  }
  return 'age-check';
}

function NewStudentOnboarding({user, onComplete}: NewStudentOnboardingProps) {
  const {data: pendingData, loading, refetch} = useCurrentUserPendingTasksQuery();
  const {data: consentData} = useCurrentUserCoppaConsentGrantsQuery();
  const performSignupStep = usePerformSignupStep();

  const pendingTasks = pendingData?.currentUser?.signupPending ?? [];
  const coppaApprovedAt = pendingData?.currentUser?.coppaApprovedAt ?? null;
  const initialStep = loading ? 'age-check' : getInitialStep(pendingTasks);

  const [currentStep, setCurrentStep] = useState<OnboardingStep | null>(null);
  const [selectedConsentOption, setSelectedConsentOption] = useState<ConsentOption | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const advancingFromCoppaRef = useRef(false);
  const [justSubmittedParentEmail, setJustSubmittedParentEmail] = useState<string | null>(null);
  const pendingParentEmail =
    justSubmittedParentEmail ??
    consentData?.currentUser?.coppaConsentGrants?.[0]?.consentEmail ??
    '';

  const activeStep = currentStep ?? initialStep;

  const navigateToStep = useCallback((step: OnboardingStep) => {
    if (step === 'consent-sub-flow') {
      window.history.pushState({onboardingStep: step}, '');
    } else {
      window.history.replaceState({onboardingStep: step}, '');
    }
    setCurrentStep(step);
  }, []);

  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      const step = e.state?.onboardingStep as OnboardingStep | undefined;
      // Only allow going back from consent-sub-flow to consent-option
      if (step === 'consent-option') {
        setCurrentStep(step);
        setSelectedConsentOption(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Completes `parental_consent` and advances to the next step.
  const advanceFromCoppaApproval = useCallback(() => {
    if (advancingFromCoppaRef.current) return;
    advancingFromCoppaRef.current = true;
    performSignupStep({
      parentalConsent: {
        consentMethod: 'parental_email',
        coppaConsentEmail: pendingParentEmail
      }
    })
      .then(() => {
        onComplete();
        navigateToStep('confirm-info');
      })
      .catch(() => {
        addGenericErrorToast();
        advancingFromCoppaRef.current = false;
      });
  }, [pendingParentEmail, performSignupStep, onComplete, navigateToStep]);

  // Auto-advance when parent approval is observed — on initial mount (hard-refresh
  // or re-login, matching the legacy flow) or via the manual "Check status" button.
  const shouldAdvanceFromCoppa =
    !!coppaApprovedAt &&
    pendingTasks.includes(SIGNUP_TASKS.PARENTAL_CONSENT) &&
    !!pendingParentEmail;

  useEffect(() => {
    if (shouldAdvanceFromCoppa) advanceFromCoppaApproval();
  }, [shouldAdvanceFromCoppa, advanceFromCoppaApproval]);

  const handleAgeSubmit = async (age: number) => {
    try {
      await performSignupStep({coppaAgeScreening: {age}});

      const needsCoppa = age < COPPA_AGE_THRESHOLD;
      navigateToStep(needsCoppa ? 'consent-option' : 'confirm-info');
    } catch (e) {
      notifier.notify(e instanceof Error ? e : new Error('Failed to complete age screening'), {
        message: 'Failed to complete age screening',
        tags: 'NewStudentOnboarding'
      });
    }
  };

  const handleConsentOptionSelect = (option: ConsentOption) => {
    setSelectedConsentOption(option);
    navigateToStep('consent-sub-flow');
  };

  const handleBackToConsentOptions = () => {
    setSelectedConsentOption(null);
    navigateToStep('consent-option');
  };

  const handleConsentComplete = () => {
    onComplete();
    navigateToStep('confirm-info');
  };

  const handleConfirmSubmit = async (data: PersonalInfoData) => {
    setIsSubmitting(true);
    setUsernameError(null);
    try {
      await performSignupStep({
        accountCompletion: {
          firstName: data.firstName,
          lastName: data.lastName,
          username: data.username
        }
      });

      onComplete();
      await refreshSession();
      await redirect();
    } catch (e: any) {
      const gqlError = e?.graphQLErrors?.[0];
      const message = gqlError?.message || e?.message || '';
      const fields: string[] = gqlError?.fields || [];

      if (
        message.toLowerCase().includes('username') ||
        fields.includes('username') ||
        message.toLowerCase().includes('already been taken')
      ) {
        setUsernameError('This username is already in use.');
      } else {
        addGenericErrorToast();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return null;

  switch (activeStep) {
    case 'age-check':
      return <AgeCheckStep onSubmit={handleAgeSubmit} />;

    case 'consent-option':
      return <ConsentOptionStep onSelectOption={handleConsentOptionSelect} />;

    case 'consent-sub-flow': {
      if (!selectedConsentOption) return null;

      switch (selectedConsentOption) {
        case 'enroll-code':
          return (
            <NewEnrollCodeStep
              onBack={handleBackToConsentOptions}
              onSubmit={handleConsentComplete}
            />
          );
        case 'class-invite':
          return (
            <NewClassInviteStep
              onBack={handleBackToConsentOptions}
              onSubmit={handleConsentComplete}
            />
          );
        case 'parental-consent':
          return (
            <NewParentalConsentStep
              onBack={handleBackToConsentOptions}
              onSubmit={(submittedEmail) => {
                setJustSubmittedParentEmail(submittedEmail);
              }}
              defaultEmail={pendingParentEmail}
              pendingParentEmail={pendingParentEmail}
              onCheckStatus={pendingParentEmail ? () => refetch() : undefined}
            />
          );
        default:
          return null;
      }
    }

    case 'confirm-info':
      return (
        <ConfirmInfoStep
          onSubmit={handleConfirmSubmit}
          isSubmitting={isSubmitting}
          usernameError={usernameError}
          // Pre-fill from the user record — populated when the student signed up via
          // Clever or Google SSO (BE captures first/last name in the OAuth callback).
          defaultFirstName={user.getFirstName?.() ?? ''}
          defaultLastName={user.getLastName?.() ?? ''}
        />
      );

    default:
      return null;
  }
}

export default NewStudentOnboarding;
